import * as PIXI from 'pixi.js';
import {
    BooleanAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
    StringOrNumberAttribute,
} from '../core/attributes';
import { Config } from '../video-data/config';
import type { Interpolation } from '../types';

import { CreationType, DynamicStylingCreationLevel } from '../enums';
import { ParentMixin } from '../core/parentMixin';
import { SerializableMixin } from '../core/serializableMixin';
import { UpdateFlowMixin } from '../core/updateFlowMixin';
import { BezierCurve } from './BezierCurve';
import type { AnimationConfig } from './AnimationCurveManager';
import type { RemoveAnchoredPoints } from './Curve';

/**
 * DynamicAnimationConfiguration manages a dynamic animation configuration. This configuration describes an animation
 * for an `object`, `alphaGraphics` or `colorGraphics` property, and it is stored as part of the `dynamicStylingConfig`
 * property on the `Container`.
 */
export class DynamicAnimationConfiguration extends UpdateFlowMixin(
    ParentMixin(SerializableMixin(PIXI.utils.EventEmitter)),
) {
    override _name = 'dynamicAnimationConfiguration';
    override _classType = 'DynamicAnimationConfiguration';

    @BooleanAttribute()
    accessor enabled = true;
    @StringAttribute({ choices: Object.values(DynamicStylingCreationLevel) })
    accessor level = DynamicStylingCreationLevel.Object;
    @StringAttribute()
    accessor property = 'alpha';
    @NumberAttribute()
    accessor duration = 300;
    @NumberAttribute()
    accessor delay = 0;
    @NumberAttribute()
    accessor staggerDelay = 0.0;
    @BooleanAttribute()
    accessor zigZagPerLine = false;
    @StringAttribute({ choices: Config.ALL_ZIG_ZAG_START_OPTIONS })
    accessor zigZagPerLineStart = Config.ZIG_ZAG_START_LEFT;
    @BooleanAttribute()
    accessor shouldOffsetPropertyValue = false;
    @BooleanAttribute()
    accessor mirrorStaggerDelay = false;
    @StringAttribute({ choices: Config.ALL_INTERPOLATION_PRESETS })
    accessor interpolation: Interpolation = Config.LINEAR_INTERPOLATION_PRESET;
    @StringOrNumberAttribute({ choices: Config.ALL_INFERRED })
    accessor startValue: number | (typeof Config.ALL_INFERRED)[number] = 0;
    @NumberAttribute()
    accessor startMulti = 1;
    @StringOrNumberAttribute({ choices: Config.ALL_INFERRED })
    accessor endValue: number | (typeof Config.ALL_INFERRED)[number] = 1;
    @NumberAttribute()
    accessor endMulti = 1;
    @StringAttribute({ choices: Config.ANIMATION_CURVE_ANCHORS, serialize: false })
    accessor anchorsToRemove: RemoveAnchoredPoints = Config.NONE_ANIMATION_CURVE_ANCHOR;

    /**
     * Build dynamic animation configuration
     */
    buildConfig(): AnimationConfig {
        if (!this.enabled) {
            return;
        }
        const driven = this.parent;
        const containerDuration = driven.container.duration / 2;

        // scale the curve to match the requested duration
        // ensure the requested duration isn't longer then half the container's duration
        const curveDuration = this.duration > containerDuration ? containerDuration : this.duration;
        const curveScale = curveDuration / containerDuration;

        // curve
        const curve = BezierCurve.fromInterpolationPreset({
            delay: this.delay,
            duration: curveDuration,
            interpolation: this.interpolation,
        })
            .scale(curveScale, true, Config.ALL_ANIMATION_CURVE_ANCHOR)
            .getCurveDescription();

        // TODO: FIX SCALE

        const fullContainerDuration = driven.container.duration;
        const percentageOffset =
            fullContainerDuration > 0 ? this.staggerDelay / fullContainerDuration : 0;
        const shouldOffsetPropertyValue = this.shouldOffsetPropertyValue; // absolute or relative
        const mirrorPercentageOffset = this.mirrorStaggerDelay;

        // range
        let startValue = this._getValuesForAttribute(this.startValue);
        startValue = startValue.map((value) => value * this.startMulti);
        let endValue = this._getValuesForAttribute(this.endValue);
        endValue = endValue.map((value) => value * this.startMulti);

        // ensure both start and end has the same length
        if (startValue.length !== endValue.length) {
            if (startValue.length > endValue.length) {
                for (let i = 1; i < startValue.length; i++) {
                    endValue.push(endValue[0]);
                }
            }
            if (endValue.length > startValue.length) {
                for (let i = 1; i < endValue.length; i++) {
                    startValue.push(startValue[0]);
                }
            }
        }

        const drivenRange = [startValue[0], endValue[0]];
        const drivenObjects = this._getLevelObjects();
        const drivenProperty = [this.property];

        return {
            mode: 'multi',
            offsetMode: 'object',
            curve,
            percentageOffset,
            shouldOffsetPropertyValue,
            mirrorPercentageOffset,
            drivenRange,
            drivenProperty,
            drivenObjects,
            duration: this.duration,
            removeAnchoredPoints: this.anchorsToRemove,
            zigZagPerLine: this.zigZagPerLine,
            zigZagPerLineStart: this.zigZagPerLineStart,
        };
    }

    _getLevelObjects() {
        return this.parent.getCreationContainersAtLevelForType(this.level, CreationType.Animation);
    }

    _getValuesForAttribute(attribute) {
        if (typeof attribute === 'string') {
            return this._getInferredValueFor(attribute);
        }
        return [attribute];
    }

    _getInferredValueFor(stringValue) {
        let values = [];
        const objects = this._getLevelObjects();
        const nObjects = objects.length;
        switch (stringValue) {
            case Config.INFERRED_HEIGHT:
                values = objects.map((object) => object.getBounds().height);
                break;
            case Config.INFERRED_HEIGHT_GAPLESS: // Gapless is not a valid use case here; default to maxHeight
            case Config.INFERRED_MAX_HEIGHT:
                const maxHeight = Math.max(...objects.map((object) => object.getBounds().height));
                values = Array(nObjects).fill(maxHeight);
                break;
            case Config.INFERRED_WIDTH:
                values = objects.map((object) => object.getBounds().width);
                break;
            case Config.INFERRED_MAX_WIDTH:
                const maxWidth = Math.max(...objects.map((object) => object.getBounds().width));
                values = Array(nObjects).fill(maxWidth);
                break;
            default:
                throw Error(`Unsupported inferred value ${stringValue}`);
        }
        return values;
    }

    override get readonlyProperties() {
        return [];
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this);
    }
}
